/**
 * FloorPlanEditor: the single public React component for the room editor.
 *
 * Owns all editing state internally (selection, drag, mode, viewport) via
 * `useCreateFloorPlanEditorState` over the framework-free `editor-model`, and
 * provides it through `FloorPlanEditorStateContext`. Every descendant reads
 * it with `useFloorPlanEditorState()`; it is never passed through props. The
 * 3D view's camera controls come from the shell's `View3DProvider` the same
 * way. The editor owns the room from `defaultRoom` on; a host shows a
 * different room by keying the editor afresh.
 *
 * Self-contained embeddable layout (fills its host): center stage
 * (2D plan / 3D dollhouse) · right Details panel. Host-app chrome
 * (branding, navigation) lives in the enclosing app, never here.
 */

import { css } from '@emotion/react';
import { useImperativeHandle, useLayoutEffect, useState } from 'react';

import { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import type { FloorTypeOption, PlanLayers } from '@kaiizen/kai-floor-plan-editor-core';
import {
  cssColor,
  type FloorPlanEditorState,
  FloorPlanEditorStateContext,
  type OpeningView,
  PALETTE,
  roomToSave,
  setPlanLayers,
  useCreateFloorPlanEditorState,
  useFloorPlanPreferences,
  useSaveHandle,
} from '@kaiizen/kai-floor-plan-editor-core';

import { AddElementButton, PlanStage, SidePanel, View3DCanvas, ViewOverlay } from './components';
import { downloadRoomModel } from './downloadRoomModel';
// Keeps the preferences in localStorage.
import './preferences';
import { fBody, ink } from './styles';
import { useStageSize } from './useStageSize';
import { useUndoRedoShortcuts } from './useUndoRedoShortcuts';
import { View3DProvider } from './view3d';

/** The component's own flex root: the editor never relies on host layout. */
const FLOOR_PLAN_EDITOR_ROOT_STYLE = css`
  label: FLOOR_PLAN_EDITOR_ROOT_STYLE;
  display: flex; width: 100%; height: 100%; min-height: 0; min-width: 0;
  font: 14px/1.4 ${fBody};
  color: ${ink};
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  /* A drag that misses a handle must never start selecting text; typing into a field still works. */
  user-select: none; -webkit-user-select: none;
  input, textarea, select { user-select: text; -webkit-user-select: text; }
`;

/** The center stage (drafting paper) hosting the 2D and 3D views. */
const STAGE_STYLE = css`
  label: STAGE_STYLE;
  position: relative; flex: 1; min-width: 0; background: ${cssColor(PALETTE.stage)};
`;

/** Open in the view the host asked for, once, before the first paint: 3D starts in 3D; 2D is where the editor starts anyway. */
const useOpeningMode = ({ setMode }: FloorPlanEditorState, view: OpeningView | undefined): void =>
  useLayoutEffect(() => {
    if (view?.mode === '3d') setMode('3d');
  }, [setMode, view]);

/** What shows inside the walls follows the view menu, before the first paint. */
const useViewMenuLayers = (floorPlanEditorState: FloorPlanEditorState, layers: PlanLayers): void =>
  useLayoutEffect(() => {
    if (floorPlanEditorState.stRef.current.layers !== layers) setPlanLayers(floorPlanEditorState, layers);
  }, [floorPlanEditorState, layers]);

/**
 * The editor's imperative surface, reached through the `ref` prop. `save()` is
 * THE save: the same action the Details panel's Save button runs, so a host
 * saving from a navigation guard produces exactly what the button would.
 */
export interface FloorPlanEditorHandle {
  /** Save now, if there is anything to save; false when the editor was already clean or has no `onSave`. */
  save: () => boolean;
}

export interface FloorPlanEditorProps {
  /** The imperative handle (React 19 ref-as-prop). */
  ref?: React.Ref<FloorPlanEditorHandle>;
  /** The room to start from. */
  defaultRoom?: FloorPlanRoom;
  /**
   * The view to open in, read once at mount like `defaultRoom`: 2D (the
   * default), or 3D looking from the given camera angle. The floor plan
   * view's “Edit room” hands over the view it shows (`onOpenRoom`).
   */
  defaultView?: OpeningView;
  /**
   * Enables the Details-panel Save button. Clicking it marks the current room
   * as the known saved version (the button greys out until the room would
   * save differently from it again, so undoing back to it also greys out) and
   * hands that room to the host, in its optimized form. Omit to hide the
   * button.
   */
  onSave?: (room: FloorPlanRoom) => void;
  /**
   * Fired whenever the unsaved-changes state flips: the very value that
   * enables the Save button (the current room would save differently from
   * the last saved one; undoing back to it reads clean again). Hosts wire
   * navigation guards to this rather than tracking changes themselves.
   */
  onDirtyChange?: (isDirty: boolean) => void;
  /**
   * The allowed flooring types, overriding the built-in list. Every flooring
   * pick list (a region's floor type, each flooring-by-type row) draws from
   * it: the option KEY is what the room stores, the LABEL is what the user
   * sees, and `id` is the host app's identifier, carried through untouched.
   * An empty list allows no type: the pick lists are disabled, and a new
   * flooring region starts unset.
   */
  floorTypes?: FloorTypeOption[];
  /** Dev/test seam: receives the editor state (used by E2E; optional). */
  floorPlanEditorStateRef?: React.MutableRefObject<FloorPlanEditorState | null>;
  /**
   * Read-only mode: the plan renders and everything stays selectable and
   * inspectable (the Details panel shows values), but nothing can be changed:
   * no dragging, no deletes/adds, no panel edits, no Save/Undo/Redo. Off by
   * default.
   */
  readOnly?: boolean;
}

export function FloorPlanEditor(
  { ref, defaultRoom, defaultView, onSave, onDirtyChange, floorTypes, floorPlanEditorStateRef, readOnly = false }: FloorPlanEditorProps,
): React.JSX.Element {
  const { snapping, layers } = useFloorPlanPreferences();
  const floorPlanEditorState = useCreateFloorPlanEditorState(defaultRoom, floorTypes, readOnly, snapping);
  const [openingView] = useState(defaultView);
  useOpeningMode(floorPlanEditorState, openingView);
  useViewMenuLayers(floorPlanEditorState, layers);
  const { stageRef, stageSize } = useStageSize();
  const { st } = floorPlanEditorState;

  if (floorPlanEditorStateRef) floorPlanEditorStateRef.current = floorPlanEditorState;

  const save = useSaveHandle(floorPlanEditorState, { onSave, onDirtyChange, readOnly }, () => roomToSave(floorPlanEditorState));
  useImperativeHandle(ref, () => ({ save }), [save]);

  useUndoRedoShortcuts(floorPlanEditorState, readOnly);

  return (
    <FloorPlanEditorStateContext.Provider value={floorPlanEditorState}>
      <View3DProvider openingAngle={openingView?.mode === '3d' ? openingView.angle : null}>
        <div css={FLOOR_PLAN_EDITOR_ROOT_STYLE} data-testid='floor-plan-editor'>
          <div css={STAGE_STYLE} ref={stageRef}>
            <PlanStage stageW={stageSize.w} stageH={stageSize.h} visible={st.mode === '2d'} />
            <View3DCanvas visible={st.mode === '3d'} />
            <ViewOverlay
              mode={st.mode}
              onMode={floorPlanEditorState.setMode}
              onExport={format => downloadRoomModel(st.draftShape ?? st.shape, format, st.layers)}
            />
            {!readOnly && <AddElementButton />}
          </div>
          <SidePanel onSave={onSave && !readOnly ? save : undefined} />
        </div>
      </View3DProvider>
    </FloorPlanEditorStateContext.Provider>
  );
}
